import { ApiFehler } from '../api/fehler';

/**
 * Kleine Helfer der Vorgangsbearbeitung (Auftrag CRM-178), geteilt von
 * Leiste, Antwort, Notizen und Folgeaktionen.
 */

const ZONE = 'Europe/Berlin';

/** „hh:mm" in der maßgeblichen Zone (FRS-01). */
export function uhrzeit(wert: string): string {
  return new Date(wert).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: ZONE });
}

/** Ein Zeitstempel als Wert für `<input type="datetime-local">`, in Berliner Zeit. */
export function alsEingabezeit(wert: string | null): string {
  if (wert === null) return '';
  const zeit = new Date(wert);
  if (Number.isNaN(zeit.getTime())) return '';
  // 'sv-SE' schreibt JJJJ-MM-TT hh:mm:ss.
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
  }).format(zeit).replace(' ', 'T').slice(0, 16);
}

/** Der Wert eines `datetime-local` als ISO-Zeitstempel; leer oder ungültig: null. */
export function ausEingabezeit(wert: string): string | null {
  if (wert.trim() === '') return null;
  const zeit = new Date(wert);
  return Number.isNaN(zeit.getTime()) ? null : zeit.toISOString();
}

/** Adressen aus einem Eingabefeld: Komma, Semikolon oder Leerraum trennen. */
export function adressen(wert: string): string[] {
  return wert.split(/[,;\s]+/).map((a) => a.trim()).filter((a) => a !== '');
}

/** SUP-20: die Platzhalter, die noch als [[name]] im Text stehen. */
export function offenePlatzhalter(text: string): string[] {
  const namen = new Set<string>();
  for (const treffer of text.matchAll(/\[\[([^\]]+)\]\]/g)) namen.add(treffer[1] ?? '');
  return [...namen].filter((n) => n !== '');
}

/**
 * Das übliche Fangen der Masken: ein ApiFehler wird zur Meldung, alles
 * andere ist ein Programmfehler und geht weiter.
 */
export function fange(setze: (f: ApiFehler | null) => void, f: unknown): void {
  setze(f instanceof ApiFehler ? f : null);
  if (!(f instanceof ApiFehler)) throw f;
}

/** SUP-19: die Grenze für Anhänge einer Antwort, zusammen. */
export const ANHANG_GRENZE = 3 * 1024 * 1024;

export function groesse(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${String(Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`;
}

/** Eine Datei als Base64 ohne den Vorspann der data:-Adresse. */
export function alsBase64(datei: Blob): Promise<string> {
  return new Promise((erfuellt, verworfen) => {
    const leser = new FileReader();
    leser.onerror = () => verworfen(leser.error ?? new Error('Die Datei ließ sich nicht lesen.'));
    leser.onload = () => {
      const ergebnis = typeof leser.result === 'string' ? leser.result : '';
      const komma = ergebnis.indexOf(',');
      erfuellt(komma < 0 ? ergebnis : ergebnis.slice(komma + 1));
    };
    leser.readAsDataURL(datei);
  });
}
